import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import CookieConsent from "@/components/CookieConsent";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import GoogleCallback from "@/pages/GoogleCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Pricing from "@/pages/Pricing";
import Dashboard from "@/pages/Dashboard";
import OpportunityDetail from "@/pages/OpportunityDetail";
import CompanyDetail from "@/pages/CompanyDetail";
import SavedSearches from "@/pages/SavedSearches";
import HiddenSignals from "@/pages/HiddenSignals";
import HiddenCompanies from "@/pages/HiddenCompanies";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AppLayout from "@/pages/AppLayout";
import Consultants from "@/pages/Consultants";
import MarketplaceLayout from "@/pages/marketplace/MarketplaceLayout";
import MarketplaceBrowse from "@/pages/marketplace/MarketplaceBrowse";
import MarketplaceRequests from "@/pages/marketplace/MarketplaceRequests";
import MarketplaceMessages from "@/pages/marketplace/MarketplaceMessages";
import MarketplaceAlerts from "@/pages/marketplace/MarketplaceAlerts";
import Tracking from "@/pages/Tracking";
import Messagerie from "@/pages/Messagerie";
import EmailSettings from "@/pages/EmailSettings";
import EmailTemplates from "@/pages/EmailTemplates";
import SettingsLayout from "@/pages/settings/SettingsLayout";
import SettingsHome from "@/pages/settings/SettingsHome";
import Profile from "@/pages/settings/Profile";
import Security from "@/pages/settings/Security";
import OrganizationSettings from "@/pages/settings/OrganizationSettings";
import NotificationsSettings from "@/pages/settings/NotificationsSettings";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminAiAnalytics from "@/pages/admin/AdminAiAnalytics";
import AdminOrganizations from "@/pages/admin/AdminOrganizations";
import AdminSources from "@/pages/admin/AdminSources";
import AdminDecisionRoles from "@/pages/admin/AdminDecisionRoles";
import AdminReview from "@/pages/admin/AdminReview";
import AdminClassification from "@/pages/admin/AdminClassification";
import AdminITMarket from "@/pages/admin/AdminITMarket";
import AdminTaxonomy from "@/pages/admin/AdminTaxonomy";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminContent from "@/pages/admin/AdminContent";
import AdminDomains from "@/pages/admin/AdminDomains";
import AdminSirene from "@/pages/admin/AdminSirene";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminProviders from "@/pages/admin/AdminProviders";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminEmails from "@/pages/admin/AdminEmails";
import AdminActivity from "@/pages/admin/AdminActivity";
import AdminMarketplace from "@/pages/admin/AdminMarketplace";
import Team from "@/pages/Team";
import AcceptInvite from "@/pages/AcceptInvite";

function App() {
  return (
    <div className="App">
      <div className="grain" />
      <BrowserRouter>
        <AuthProvider>
          <AnalyticsTracker />
          <CookieConsent />
          <Toaster theme="dark" position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/google" element={<GoogleCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />

            <Route
              path="/app"
              element={<ProtectedRoute><AppLayout /></ProtectedRoute>}
            >
              <Route index element={<Dashboard />} />
              <Route path="consultants" element={<Consultants />} />
              <Route path="marketplace" element={<MarketplaceLayout />}>
                <Route index element={<MarketplaceBrowse />} />
                <Route path="requests" element={<MarketplaceRequests />} />
                <Route path="messages" element={<MarketplaceMessages />} />
                <Route path="alerts" element={<MarketplaceAlerts />} />
              </Route>
              <Route path="tracking" element={<Tracking />} />
              <Route path="messagerie" element={<Messagerie />} />
              <Route path="crm/emails" element={<Navigate to="/app/messagerie" replace />} />
              <Route path="settings" element={<SettingsLayout />}>
                <Route index element={<SettingsHome />} />
                <Route path="profile" element={<Profile />} />
                <Route path="email" element={<EmailSettings />} />
                <Route path="templates" element={<EmailTemplates />} />
                <Route path="organization" element={<OrganizationSettings />} />
                <Route path="notifications" element={<NotificationsSettings />} />
                <Route path="security" element={<Security />} />
              </Route>
              <Route path="team" element={<Team />} />
              <Route path="opportunity/:id" element={<OpportunityDetail />} />
              <Route path="company/:id" element={<CompanyDetail />} />
              <Route path="saved" element={<SavedSearches />} />
              <Route path="hidden-signals" element={<HiddenSignals />} />
              <Route path="hidden-companies" element={<HiddenCompanies />} />
              <Route path="billing" element={<Billing />} />
            </Route>

            <Route
              path="/admin"
              element={<AdminRoute><AdminLayout /></AdminRoute>}
            >
              <Route index element={<AdminDashboard />} />
              <Route path="ai-analytics" element={<AdminAiAnalytics />} />
              <Route path="organizations" element={<AdminOrganizations />} />
              <Route path="marketplace" element={<AdminMarketplace />} />
              <Route path="sources" element={<AdminSources />} />
              <Route path="decision-roles" element={<AdminDecisionRoles />} />
              <Route path="review" element={<AdminReview />} />
              <Route path="classification" element={<AdminClassification />} />
              <Route path="it-market" element={<AdminITMarket />} />
              <Route path="taxonomy" element={<AdminTaxonomy />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="domains" element={<AdminDomains />} />
              <Route path="sirene" element={<AdminSirene />} />
              <Route path="integrations" element={<AdminIntegrations />} />
              <Route path="providers" element={<AdminProviders />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="emails" element={<AdminEmails />} />
              <Route path="activity" element={<AdminActivity />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
